import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { LastAppCookieService } from '../../services/last-app-cookie.service';
import { parseSignedOutReason, safeReturnPath, SignedOutReason } from '../../utils/signed-out-utils/signed-out-utils';
import { SIGNED_OUT_CONFIG } from './signed-out.config';

const TEXT_KEYS: Record<SignedOutReason, { title: string; body: string; button: string }> = {
  logout: { title: 'SIGNED_OUT_LOGOUT_TITLE', body: 'SIGNED_OUT_LOGOUT_BODY', button: 'SIGNED_OUT_LOG_IN_AGAIN' },
  expired: { title: 'SIGNED_OUT_EXPIRED_TITLE', body: 'SIGNED_OUT_EXPIRED_BODY', button: 'SIGNED_OUT_LOG_IN_AGAIN' },
  error: { title: 'SIGNED_OUT_ERROR_TITLE', body: 'SIGNED_OUT_ERROR_BODY', button: 'RETRY' },
};

/** Clients read what they can look at again after logging in, staff read that their session ended. */
const CROWNDESK_LOGOUT_BODY = 'SIGNED_OUT_CROWNDESK_LOGOUT_BODY';

/** Five clicks on the background crown within this time set off the easter egg: a golden shine sweeps over it. */
const EASTER_EGG_CLICKS = 5;
const EASTER_EGG_WINDOW_MS = 2000;
/** How long the shine takes; also how long further clicks are ignored, so a running shine never restarts halfway. */
const SHINE_MS = 1000;

const CROWN_PATH =
  'M12.7 29.4L7.2 19.2L20 21.9L26.7 8.7L32.9 22.1L46.1 19.3L40.8 29.4H45.2L53.3 13.9L35.3 17.5H35.2L26.6 0L17.9 17.5L0 13.9L8.1 29.4H12.7Z';

/** Public landing page for a visitor without a session. Never starts a login by itself, so it cannot loop. */
@Component({
  selector: 'rc-signed-out',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, ButtonComponent],
  templateUrl: './signed-out.component.html',
  styleUrl: './signed-out.component.scss',
  host: { '[class.rc-signed-out--desk]': 'isDesk' },
})
export class SignedOutComponent {
  readonly #auth = inject(AuthService);
  readonly #lastAppCookie = inject(LastAppCookieService);
  readonly #translate = inject(TranslateService);
  readonly #params = inject(ActivatedRoute).snapshot.queryParamMap;
  protected readonly config = inject(SIGNED_OUT_CONFIG);

  protected readonly isDesk = this.config.brand === 'crowndesk';
  protected readonly reason = parseSignedOutReason(this.#params.get('reason'));
  protected readonly text = TEXT_KEYS[this.reason];
  protected readonly body = this.isDesk && this.text === TEXT_KEYS.logout ? CROWNDESK_LOGOUT_BODY : this.text.body;
  protected readonly crownPath = CROWN_PATH;
  protected readonly year = new Date().getFullYear();
  protected readonly languages = ['nl', 'fr'] as const;
  protected readonly language = signal<'nl' | 'fr'>(navigator.language.toLowerCase().startsWith('fr') ? 'fr' : 'nl');
  protected readonly loggingIn = signal(false);

  private readonly shine = viewChild<ElementRef<SVGAnimationElement>>('shine');
  protected readonly shineDuration = `${SHINE_MS}ms`;
  // With <base href="/"> a bare url(#id) points at the app root, not this page: name the page itself
  protected readonly shineFill = `url(${location.href.split('#')[0]}#rc-signed-out-shine)`;
  #lastShine = 0;
  #crownClicks: number[] = [];

  constructor() {
    // No session means no user language yet: follow the browser, like the login page does.
    this.#translate.use(this.language());
  }

  protected useLanguage(lang: 'nl' | 'fr') {
    this.language.set(lang);
    this.#translate.use(lang);
  }

  protected logIn() {
    // The signal alone would let a fast double click through before the button re-renders as disabled.
    if (this.loggingIn()) {
      return;
    }
    this.loggingIn.set(true);
    const { lastApp, lastAppCookieDomain, lastAppCookieName } = this.config;
    if (lastApp && lastAppCookieDomain) {
      this.#lastAppCookie.write(lastApp, lastAppCookieDomain, lastAppCookieName);
    }
    const target = safeReturnPath(this.#params.get('returnTo')) ?? this.config.startPath;
    this.#auth
      .loginWithRedirect({ appState: { target }, authorizationParams: { prompt: 'login' } })
      .subscribe({ error: () => this.loggingIn.set(false) });
  }

  // A double or triple click selects text, here the text near the crown: the easter egg's rapid clicks would do that
  protected onCrownMouseDown(event: MouseEvent) {
    if (event.detail > 1) {
      event.preventDefault();
    }
  }

  protected onCrownClick() {
    const now = Date.now();
    this.#crownClicks = [...this.#crownClicks.filter((at) => now - at < EASTER_EGG_WINDOW_MS), now];
    if (this.#crownClicks.length < EASTER_EGG_CLICKS) {
      return;
    }
    this.#crownClicks = [];
    // Not again while a sweep still runs, and not at all for people who asked for less motion
    if (now - this.#lastShine < SHINE_MS || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    this.#lastShine = now;
    this.shine()?.nativeElement.beginElement();
  }

  // Back from Auth0, the browser may restore this page from its back-forward cache, button still disabled.
  @HostListener('window:pageshow')
  protected onPageShow() {
    this.loggingIn.set(false);
  }
}
